// src/components/HomePage.tsx
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  onAuthStateChanged,
  signInAnonymously,
  signInWithCustomToken,
} from 'firebase/auth';
import {
  query,
  collection,
  getDocs,
  orderBy,
  limit,
  where,
  DocumentReference,
  getCountFromServer,
} from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faMapMarkerAlt,
  faTimes,
  faGlobe,
} from '@fortawesome/free-solid-svg-icons';

import { db, auth, firebaseAppId } from './../../firebase';
import {
  Spinner,
  LoadingMessage,
  ErrorMessage,
  SearchBarContainer,
  SearchIcon,
  SearchInput,
  LocationSelectContainer,
  LocationIcon,
  LocationDropdown,
  LocationOption,
  SidebarToggleContainer,
  MainLayout,
  ContentContainer,
  SidebarToggle,
  SidebarContainer,
  CategoryButton,
  SidebarHeading,
  CategoryIcon,
  CategoryButtonContent,
  CategoryNameContainer,
  FloatingButtonsContainer,
  FloatingButton,
  CenteredHeading,
  PremiumStallsGrid,
  ListingsGrid,
  SkeletonGrid,
  SkeletonCard,
  SkeletonText,
  SkeletonImage,
  NoPostsMessage,
  UnifiedSearchBarWrapper,
  HomeWrapper,
  RealEstateSidebarWrapper,
  MarketplaceSidebarWrapper,
  WelcomePopupOverlay,
  WelcomeMessage,
  WelcomeCloseButton,
  WelcomePopupContent,
  WelcomeTitle,
  WelcomeButton,
} from './styles/home.styles';
import { styled } from 'styled-components';
import { categoryIcons, realEstateCategories, stallCategories } from './homepage/categories';
import { PremiumStallCard, StallCard } from './cards/StallCards';
import { RealEstateCard } from './cards/RealEstateCard';

declare const __initial_auth_token: string | undefined;
declare const __app_id: string;

/// --- Interfaces ----------------------------------------------------
export interface ListingMedia {
  url: string;
  description: string;
  price?: number;
  type: 'image' | 'video';
}
export interface Listing {
  mediaDetails: ListingMedia[];
}

export interface StallDisplayData {
  id: string;
  title: string;
  description: string;
  category: string;
  StallNumber: string;
  listings: Listing[];
  price: number | null;
  location: string;
  contact: string;
  mediaDetails: ListingMedia[];
  createdAt?: Date;
  isPremium?: boolean;
  email?: string;
  businessPhone?: string;
  whatsappPhone?: string;
  tiktokLink?: string;
  facebookLink?: string;
  xTwitterLink?: string;
  youtubeLink?: string;
  instagramLink?: string;
  contactPerson?: string;
  website?: string;
  stallType?: string;
  subscriptionExpiryDate?: Date;
  profileImageUrl?: string | null;
  views?: number;
}

export interface RealEstateListing {
  id: string;
  name: string;
  category: string;
  price: number;
  villageName?: string;
  latitude?: number;
  longitude?: number;
  imageUrls: string[];
  videoUrls: string[];
  description?: string;
  createdAt: Date;
}

interface JobAdvert {
  id: string;
  jobTitle: string;
  organization: string;
  dutyStation: string;
  description: string;
  responsibilities: string[];
  benefits: string[];
  requirements: string[];
  contactNumber: string;
  applicationDeadline: string;
  status: 'active' | 'inactive';
  createdAt?: Date;
}

interface AdminUnit {
  id: string;
  Village: string;
  'Parish/Ward': string;
  'Subcounty/Division': string;
  'District/City': string;
  Region: string;
}

// --- Styled Count Badge ---
const CountBadge = styled.span`
  opacity: 0.9;
  font-size: 0.88em;
  font-weight: 700;
  margin-left: 5px;
  color: ${({ theme }) => theme.colors.text || '#333'};
`;

const JobOpportunitiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
`;

const JobOpportunityCard = styled.article`
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const JobOpportunityTitle = styled.h3`
  margin: 0 0 8px;
  color: #007bff;
  font-size: 1.1rem;
`;

const JobOpportunityText = styled.p`
  margin: 0 0 8px;
  color: #555;
  line-height: 1.5;
`;

const JobOpportunityMeta = styled.p`
  margin: 0 0 8px;
  color: #777;
  font-size: 0.9rem;
`;

// --- Sidebar Header ---
const SidebarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  font-size: 1.5rem;
  padding: 5px;
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }

  @media (min-width: 1025px) {
    display: none;
  }
`;


interface SidebarProps {
  categories: string[];
  selectedCategory: string;
  onCategorySelect: (c: string) => void;
  title: string;
  onClose?: () => void;
  categoryCounts?: Record<string, number>;
  totalInSection: number;
}

// --- Marketplace Sidebar (Stalls Only) ---
const MarketplaceSidebar: React.FC<SidebarProps> = React.memo(({
  categories,
  selectedCategory,
  onCategorySelect,
  title,
  onClose,
  categoryCounts = {},
  totalInSection,
}) => {
  const sortedCategories = useMemo(() => {
    return [...categories].sort((a, b) => (categoryCounts[b] || 0) - (categoryCounts[a] || 0));
  }, [categories, categoryCounts]);

  return (
    <SidebarContainer>
      <SidebarHeader>
        <SidebarHeading>{title}</SidebarHeading>
        <CloseButton onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </CloseButton>
      </SidebarHeader>

      <CategoryButton
        $isActive={selectedCategory === 'All Categories'}
        onClick={() => onCategorySelect('All Categories')}
      >
        <CategoryButtonContent>
          <CategoryNameContainer>
            <CategoryIcon icon={faGlobe} $isActive={selectedCategory === 'All Categories'} />
            All Categories
            {totalInSection > 0 && <CountBadge>({totalInSection})</CountBadge>}
          </CategoryNameContainer>
        </CategoryButtonContent>
      </CategoryButton>

      {sortedCategories.map((c) => {
        const count = categoryCounts[c] || 0;
        return (
          <CategoryButton
            key={c}
            $isActive={selectedCategory === c}
            onClick={() => onCategorySelect(c)}
          >
            <CategoryButtonContent>
              <CategoryNameContainer>
                <CategoryIcon
                  icon={categoryIcons[c] ?? faGlobe}
                  $isActive={selectedCategory === c}
                />
                {c}
                {count > 0 && <CountBadge>({count})</CountBadge>}
              </CategoryNameContainer>
            </CategoryButtonContent>
          </CategoryButton>
        );
      })}
    </SidebarContainer>
  );
});

const Sidebar: React.FC<SidebarProps> = React.memo(
  ({
    categories,
    selectedCategory,
    onCategorySelect,
    title,
    onClose,
    categoryCounts = {},
    totalInSection,
  }) => {
    const sortedCategories = useMemo(() => {
      return [...categories]
        .filter((c) => c !== 'All Categories')
        .sort((a, b) => (categoryCounts[b] || 0) - (categoryCounts[a] || 0));
    }, [categories, categoryCounts]);

    return (
      <SidebarContainer>
        <SidebarHeader>
          <SidebarHeading>{title}</SidebarHeading>
          {onClose && (
            <CloseButton onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} />
            </CloseButton>
          )}
        </SidebarHeader>

        <CategoryButton
          $isActive={selectedCategory === 'All Categories'}
          onClick={() => onCategorySelect('All Categories')}
        >
          <CategoryButtonContent>
            <CategoryNameContainer>
              <CategoryIcon icon={faGlobe} $isActive={selectedCategory === 'All Categories'} />
              All Categories
              {totalInSection > 0 && <CountBadge>({totalInSection})</CountBadge>}
            </CategoryNameContainer>
          </CategoryButtonContent>
        </CategoryButton>

        {sortedCategories.map((c) => {
          const count = categoryCounts[c] || 0;
          return (
            <CategoryButton
              key={c}
              $isActive={selectedCategory === c}
              onClick={() => onCategorySelect(c)}
            >
              <CategoryButtonContent>
                <CategoryNameContainer>
                  <CategoryIcon
                    icon={categoryIcons[c] ?? faGlobe}
                    $isActive={selectedCategory === c}
                  />
                  {c}
                  <CountBadge>({count})</CountBadge>
                </CategoryNameContainer>
              </CategoryButtonContent>
            </CategoryButton>
          );
        })}
      </SidebarContainer>
    );
  }
);

// --- Real Estate Sidebar (Real Estate Only) ---
const RealEstateSidebarComponent: React.FC<SidebarProps> = React.memo(({
  categories,
  selectedCategory,
  onCategorySelect,
  title,
  onClose,
  categoryCounts = {},
  totalInSection,
}) => {
  const sortedCategories = useMemo(() => {
    return [...categories].sort((a, b) => (categoryCounts[b] || 0) - (categoryCounts[a] || 0));
  }, [categories, categoryCounts]);

  return (
    <SidebarContainer>
      <SidebarHeader>
        <SidebarHeading>{title}</SidebarHeading>
        <CloseButton onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </CloseButton>
      </SidebarHeader>

      <CategoryButton
        $isActive={selectedCategory === 'All Categories'}
        onClick={() => onCategorySelect('All Categories')}
      >
        <CategoryButtonContent>
          <CategoryNameContainer>
            <CategoryIcon icon={faGlobe} $isActive={selectedCategory === 'All Categories'} />
            All Categories
            {totalInSection > 0 && <CountBadge>({totalInSection})</CountBadge>}
          </CategoryNameContainer>
        </CategoryButtonContent>
      </CategoryButton>

      {sortedCategories.map((c) => {
        const count = categoryCounts[c] || 0;
        return (
          <CategoryButton
            key={c}
            $isActive={selectedCategory === c}
            onClick={() => onCategorySelect(c)}
          >
            <CategoryButtonContent>
              <CategoryNameContainer>
                <CategoryIcon
                  icon={categoryIcons[c] ?? faGlobe}
                  $isActive={selectedCategory === c}
                />
                {c}
                {count > 0 && <CountBadge>({count})</CountBadge>}
              </CategoryNameContainer>
            </CategoryButtonContent>
          </CategoryButton>
        );
      })}
    </SidebarContainer>
  );
});

// --- NEW CONTAINER FOR LEFT-ALIGNED FLOATING BUTTON ---
const FloatingButtonLeftContainer = styled.div`
  position: fixed;
  bottom: 20px;
  left: 20px;
  display: flex;
  z-index: 1000;
`;

// --- Real Estate Collection Mapping (FIXED: ALL CATEGORIES) ---
const realEstateCollectionMap: Record<string, string> = {
  'Land': 'land',
  'Residential Houses': 'residential_houses',
  'Equipment': 'equipment',
  'Buildings (Shopping Malls , Arcades, Apartment , Hotels , Restaurants , Schools , Hospitals , Rentals &Leasing)': 'buildings_shopping_malls_arcades_apartment_hotels_restaurants_schools_hospitals_rentals_leasing_',
  'Cars': 'cars',
  'General Property Sales': 'general_property',
  'Property Management': 'property_management',
};

const realEstateListedCategories = realEstateCategories.filter(
  (category) => category !== 'Job Opportunities'
);

// Helper: get actual Firestore collection name
const getRealEstateCollectionName = (category: string): string => {
  const mapped = realEstateCollectionMap[category];
  if (!mapped) {
    console.warn(`[Home] No collection mapped for Real Estate category: "${category}". Falling back to sanitized name.`);
    return category.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_{2,}/g, '_');
  }
  return mapped;
};

// --- Home -----------------------------------------------------------
interface HomeProps {
  selectedCategory: string;
  setSelectedCategory: (c: string) => void;
}

export const Home: React.FC<HomeProps> = ({
  selectedCategory,
  setSelectedCategory,
}) => {
  const navigate = useNavigate();

  // ---------- state ----------
  const [globalSearchQuery, setGlobalSearchQuery] = useState('');
  const [locationSearchQuery, setLocationSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('All Locations');

  const [loadingFirebase, setLoadingFirebase] = useState(true);
  const [firebaseError, setFirebaseError] = useState<string | null>(null);

  const [stallsWithLatestListing, setStallsWithLatestListing] = useState<StallDisplayData[]>([]);
  const [loadingStalls, setLoadingStalls] = useState(true);
  const [stallsError, setStallsError] = useState<string | null>(null);
  const [initialStallsLoaded, setInitialStallsLoaded] = useState(false);

  const [realEstateListings, setRealEstateListings] = useState<RealEstateListing[]>([]);
  const [loadingRealEstate, setLoadingRealEstate] = useState(true);
  const [realEstateError, setRealEstateError] = useState<string | null>(null);

  const [jobAdverts, setJobAdverts] = useState<JobAdvert[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [jobsError, setJobsError] = useState<string | null>(null);

  const [isMarketplaceSidebarOpen, setIsMarketplaceSidebarOpen] = useState(false);
  const [isRealEstateSidebarOpen, setIsRealEstateSidebarOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1024);

  const [adminUnits, setAdminUnits] = useState<AdminUnit[]>([]);

  const [shuffledPremiumStalls, setShuffledPremiumStalls] = useState<StallDisplayData[]>([]);
  const [shuffleCount, setShuffleCount] = useState(0);
  const [loadingTimeout, setLoadingTimeout] = useState(false);

  const [persistentCategoryCounts, setPersistentCategoryCounts] = useState<Record<string, number>>({});

  const abortControllerRef = useRef<AbortController | null>(null);

  // ---------- resize ----------
  useEffect(() => {
    const onResize = () => {
      setIsDesktop(window.innerWidth > 1024);
      if (window.innerWidth > 1024) {
        setIsMarketplaceSidebarOpen(false);
        setIsRealEstateSidebarOpen(false);
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ---------- type guard ----------
  const isStall = (item: StallDisplayData | RealEstateListing): item is StallDisplayData =>
    'isPremium' in item;

  // ---------- totals per section ----------
  const totalStalls = useMemo(() => {
    return stallCategories.reduce((sum, cat) => sum + (persistentCategoryCounts[cat] || 0), 0);
  }, [persistentCategoryCounts]);

  const totalRealEstate = useMemo(() => {
    return realEstateCategories.slice(1).reduce((sum, cat) => sum + (persistentCategoryCounts[cat] || 0), 0);
  }, [persistentCategoryCounts]);

  // ---------- load stalls ----------
  const loadStalls = useCallback(
    async (cat: string) => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setLoadingStalls(true);
      setStallsWithLatestListing([]);
      setInitialStallsLoaded(false);

      const stallsRef = collection(db, 'stalls');
      const q =
        cat === 'All Categories'
          ? query(stallsRef, orderBy('createdAt', 'desc'), limit(50))
          : query(stallsRef, where('category', '==', cat), orderBy('createdAt', 'desc'), limit(12));

      try {
        const snap = await getDocs(q);
        
        // Optimization: Fetch all sub-collections in parallel and update state once
        // to prevent multiple re-renders and layout thrashing during load.
        const results = await Promise.all(
            snap.docs.map(async (doc) => {
              const data = doc.data();
              
              // Fetch listings immediately to avoid secondary re-renders
              let listings: Listing[] = [];
              let mediaDetails: ListingMedia[] = [];
              try {
                const listingsRef = collection(doc.ref, 'listings');
                // Optimization: Limit to 3 to reduce bandwidth/processing on mobile
                const lq = query(listingsRef, orderBy('createdAt', 'desc'), limit(3));
                const lsnap = await getDocs(lq);
                listings = lsnap.docs.map((ld) => {
                  const d = ld.data();
                  const media: ListingMedia[] = [];
                  // Optimization: Only grab necessary media for preview
                  d.images?.slice(0, 2).forEach((url: string) =>
                    media.push({ url, description: d.description ?? '', type: 'image', price: d.price })
                  );
                  d.videos?.slice(0, 1).forEach((url: string) =>
                    media.push({ url, description: d.description ?? '', type: 'video', price: d.price })
                  );
                  return { ...d, mediaDetails: media } as Listing;
                });
                mediaDetails = listings.flatMap((l) => l.mediaDetails);
              } catch (err) {
                // Ignore listing fetch errors
              }

              return {
                id: doc.id,
                title: data.businessName ?? 'Untitled Stall',
                description: data.additionalDetails ?? '',
                category: data.category ?? 'Other',
                StallNumber: data.stallNumber ?? 'N/A',
                listings: listings,
                price: null,
                location: data.actualLocation ?? 'N/A',
                contact: data.businessPhone ?? 'N/A',
                mediaDetails: mediaDetails,
                createdAt: data.createdAt?.toDate(),
                isPremium: data.stallType === 'Premium',
                email: data.email ?? '',
                businessPhone: data.businessPhone ?? '',
                whatsappPhone: data.whatsappPhone ?? '',
                tiktokLink: data.tiktokLink ?? '',
                facebookLink: data.facebookLink ?? '',
                xTwitterLink: data.xTwitterLink ?? '',
                youtubeLink: data.youtubeLink ?? '',
                instagramLink: data.instagramLink ?? '',
                contactPerson: data.contactPerson ?? '',
                website: data.website ?? '',
                stallType: data.stallType ?? '',
                subscriptionExpiryDate: data.subscriptionExpiryDate?.toDate(),
                profileImageUrl: data.profileImageUrl ?? null,
                views: typeof data.views === 'number' ? data.views : 0,
              } as StallDisplayData;
            })
        );

        if (!controller.signal.aborted) {
            setStallsWithLatestListing(results);
            setInitialStallsLoaded(true);
        }
      } catch (e: any) {
        if (e.name !== 'CanceledError') setStallsError(`Failed to load stalls: ${e.message}`);
      } finally {
        if (!controller.signal.aborted) setLoadingStalls(false);
      }
    },
    []
  );

  // ---------- load real estate ----------
  const loadRealEstate = useCallback(async (cats: string[]) => {
    setLoadingRealEstate(true);
    setRealEstateListings([]);

    const promises = cats.map(async (cat) => {
      const collName = getRealEstateCollectionName(cat);
      const ref = collection(db, `artifacts/${firebaseAppId}/${collName}`);
      const q = query(ref, orderBy('createdAt', 'desc'), limit(6));
      try {
        const snap = await getDocs(q);
        return snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            name: data.name ?? 'Untitled Property',
            category: data.category ?? 'Unknown',
            price: data.price ?? 0,
            villageName: data.villageName,
            latitude: data.latitude,
            longitude: data.longitude,
            imageUrls: Array.isArray(data.imageUrls) ? data.imageUrls : [],
            videoUrls: Array.isArray(data.videoUrls) ? data.videoUrls : [],
            description: data.description,
            createdAt: data.createdAt?.toDate() ?? new Date(),
          } as RealEstateListing;
        });
      } catch (err) {
        console.error(`[Home] Failed to load ${cat} from ${collName}:`, err);
        return [];
      }
    });

    try {
      const all = (await Promise.all(promises)).flat();
      setRealEstateListings(all);
    } catch (e: any) {
      setRealEstateError('Failed to load real estate listings.');
    } finally {
      setLoadingRealEstate(false);
    }
  }, []);

  const loadJobAdverts = useCallback(async () => {
    setLoadingJobs(true);
    setJobsError(null);
    setJobAdverts([]);

    try {
      const q = query(
        collection(db, 'jobAdverts'),
        where('status', '==', 'active'),
        orderBy('createdAt', 'desc'),
        orderBy('__name__', 'asc'),
        limit(12)
      );
      const snapshot = await getDocs(q);
      setJobAdverts(snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() ?? new Date(),
        } as JobAdvert;
      }));
    } catch (err) {
      console.error('[Home] Failed to load job opportunities:', err);
      setJobsError('Failed to load job opportunities.');
    } finally {
      setLoadingJobs(false);
    }
  }, []);

const [showWelcomePopup, setShowWelcomePopup] = useState(false);
const welcomeTimerRef = useRef<number | null>(null);

useEffect(() => {
  const lastClosedAt = localStorage.getItem('grema_welcome_last_closed_at');
  const now = Date.now();
  const oneHourMs = 1000 * 60 * 60;

  const shouldShowPopup = !lastClosedAt || now - Number(lastClosedAt) > oneHourMs;
  if (shouldShowPopup) {
    setShowWelcomePopup(true);
  }
}, []);

const closeWelcomePopup = useCallback(() => {
  setShowWelcomePopup(false);
  localStorage.setItem('grema_welcome_last_closed_at', Date.now().toString());
  if (welcomeTimerRef.current) {
    window.clearTimeout(welcomeTimerRef.current);
    welcomeTimerRef.current = null;
  }
}, []);

useEffect(() => {
  if (!showWelcomePopup) {
    return;
  }

  welcomeTimerRef.current = window.setTimeout(() => {
    setShowWelcomePopup(false);
    localStorage.setItem('grema_welcome_last_closed_at', Date.now().toString());
  }, 10000);

  return () => {
    if (welcomeTimerRef.current) {
      window.clearTimeout(welcomeTimerRef.current);
      welcomeTimerRef.current = null;
    }
  };
}, [showWelcomePopup]);

  // ---------- auth + parallel start + fetch all category counts ----------
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        try {
          if (typeof __initial_auth_token !== 'undefined' && __initial_auth_token) {
            await signInWithCustomToken(auth, __initial_auth_token);
          } else {
            await signInAnonymously(auth);
          }
          setFirebaseError(null);
        } catch (e: any) {
          setFirebaseError(`Authentication failed: ${e.message}`);
        }
      }
      setLoadingFirebase(false);

      loadStalls('All Categories');
      loadRealEstate(realEstateListedCategories);
      loadJobAdverts();

      // === FETCH ALL CATEGORY COUNTS (FIXED) ===
      const fetchAllCategoryCounts = async () => {
        // Defer count fetching to prioritize main content loading
        setTimeout(async () => {
            const counts: Record<string, number> = {};
            [...stallCategories, ...realEstateListedCategories, 'Job Opportunities'].forEach((cat) => {
              counts[cat] = 0;
            });
    
            try {
              // 1. Count stalls
              const stallQueries = stallCategories.map((cat) =>
                getCountFromServer(query(collection(db, 'stalls'), where('category', '==', cat))).then((snap) => ({
                  cat,
                  count: snap.data().count,
                }))
              );
    
              // 2. Count real estate (FIXED: use mapping)
              const realEstateQueries = realEstateListedCategories.map((displayCat) => {
                const collName = getRealEstateCollectionName(displayCat);
                const collPath = `artifacts/${firebaseAppId}/${collName}`;
                return getCountFromServer(query(collection(db, collPath)))
                  .then((snap) => ({
                    cat: displayCat,
                    count: snap.data().count,
                  }))
                  .catch((err) => {
                    console.error(`[Count] Failed for ${displayCat} at ${collPath}:`, err);
                    return { cat: displayCat, count: 0 };
                  });
              });

              const jobCountQuery = getCountFromServer(
                query(collection(db, 'jobAdverts'), where('status', '==', 'active'))
              ).then((snap) => ({
                cat: 'Job Opportunities',
                count: snap.data().count,
              }));
    
              const [stallResults, reResults, jobResult] = await Promise.all([
                Promise.all(stallQueries),
                Promise.all(realEstateQueries),
                jobCountQuery,
              ]);
    
              stallResults.forEach(({ cat, count }) => {
                counts[cat] = count;
              });
              reResults.forEach(({ cat, count }) => {
                counts[cat] = count;
              });
              counts[jobResult.cat] = jobResult.count;
    
              // console.log('[Home] Final category counts:', counts); // DEBUG
              setPersistentCategoryCounts(counts);
            } catch (err) {
              console.error('[Home] Failed to fetch category counts', err);
            }
        }, 2000); // Delay by 2 seconds
      };

      fetchAllCategoryCounts();
    });
    return unsub;
  }, [loadStalls, loadRealEstate, loadJobAdverts]);

  // ---------- category change ----------
  useEffect(() => {
    if (loadingFirebase) return;

    if (selectedCategory === 'Job Opportunities') {
      setStallsWithLatestListing([]);
      setLoadingStalls(false);
      setInitialStallsLoaded(true);
      setRealEstateListings([]);
      setLoadingRealEstate(false);
      loadJobAdverts();
      return;
    }

    if (selectedCategory === 'All Categories' || stallCategories.includes(selectedCategory)) {
      loadStalls(selectedCategory);
    } else {
      setStallsWithLatestListing([]);
      setLoadingStalls(false);
      setInitialStallsLoaded(true);
    }

    if (selectedCategory === 'All Categories' || realEstateListedCategories.includes(selectedCategory)) {
      const cats =
        selectedCategory === 'All Categories'
          ? realEstateListedCategories
          : [selectedCategory];
      loadRealEstate(cats);
    } else {
      setRealEstateListings([]);
      setLoadingRealEstate(false);
    }

    setJobAdverts([]);
    setLoadingJobs(false);
    setJobsError(null);
  }, [selectedCategory, loadingFirebase, loadStalls, loadRealEstate, loadJobAdverts]);

  // ---------- admin units ----------
  useEffect(() => {
    if (!loadingFirebase && initialStallsLoaded) {
      (async () => {
        try {
          const snap = await getDocs(collection(db, 'administrativeUnits'));
          setAdminUnits(snap.docs.map((d) => ({ ...d.data(), id: d.id }) as AdminUnit));
        } catch (_) {}
      })();
    }
  }, [loadingFirebase, initialStallsLoaded]);

  // ---------- locations ----------
  const allLocations = useMemo(() => {
    const set = new Set<string>();

    stallsWithLatestListing.forEach((s) => {
      if (s.location && s.location !== 'N/A' && typeof s.location === 'string') {
        set.add(s.location);
      }
    });

    realEstateListings.forEach((r) => {
      if (r.villageName && typeof r.villageName === 'string') {
        set.add(r.villageName);
      }
    });

    adminUnits.forEach((u) => {
      if (u.Village && typeof u.Village === 'string') {
        set.add(u.Village);
      }
    });

    const sorted = ['All Locations', ...Array.from(set).sort()];

    if (locationSearchQuery) {
      const lc = locationSearchQuery.toLowerCase();
      return sorted.filter((l) => typeof l === 'string' && l.toLowerCase().includes(lc));
    }

    return sorted;
  }, [stallsWithLatestListing, realEstateListings, adminUnits, locationSearchQuery]);

  // ---------- filtered + sorted ----------
  const filteredAndSortedItems = useMemo(() => {
    let items: (StallDisplayData | RealEstateListing)[] = [];

    if (selectedCategory === 'All Categories') {
      items = [...stallsWithLatestListing, ...realEstateListings];
    } else if (realEstateCategories.includes(selectedCategory)) {
      items = realEstateListings.filter((i) => i.category === selectedCategory);
    } else {
      items = stallsWithLatestListing.filter((i) => i.category === selectedCategory);
    }

    const q = globalSearchQuery.toLowerCase();
    if (q) {
      items = items.filter((item) => {
        if (isStall(item)) {
          const fields = [
            item.title,
            item.description,
            item.category,
            item.location,
            item.businessPhone,
            item.whatsappPhone,
            item.email,
            item.id,
          ];
          return fields.some((f) => typeof f === 'string' && f.toLowerCase().includes(q));
        } else {
          const fields = [item.name, item.description, item.category, item.villageName];
          return fields.some((f) => typeof f === 'string' && f.toLowerCase().includes(q));
        }
      });
    }

    if (selectedLocation !== 'All Locations') {
      items = items.filter((item) => {
        if (isStall(item)) return item.location === selectedLocation;
        return item.villageName === selectedLocation;
      });
    }

    return items.sort((a, b) => (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0));
  }, [
    stallsWithLatestListing,
    realEstateListings,
    globalSearchQuery,
    selectedCategory,
    selectedLocation,
  ]);

  // ---------- premium shuffle ----------
  const shuffleArray = (arr: StallDisplayData[]) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  useEffect(() => {
    const premium = filteredAndSortedItems.filter((i) => isStall(i) && i.isPremium) as StallDisplayData[];
    if (premium.length) {
      setShuffledPremiumStalls(shuffleArray(premium));
      const id = setInterval(() => {
        setShuffledPremiumStalls((prev) => {
          if (prev.length <= 1) return prev;
          const [top, ...rest] = prev;
          const idx = Math.floor(Math.random() * rest.length);
          const newTop = rest[idx];
          const others = rest.filter((_, i) => i !== idx);
          return [newTop, top, ...others];
        });
        setShuffleCount((c) => c + 1);
      }, 15_000);
      return () => clearInterval(id);
    } else {
      setShuffledPremiumStalls([]);
    }
  }, [filteredAndSortedItems]);

  // ---------- loading timeout ----------
  useEffect(() => {
    let id: NodeJS.Timeout;
    if (loadingStalls || loadingRealEstate) {
      id = setTimeout(() => setLoadingTimeout(true), 10_000);
    } else {
      setLoadingTimeout(false);
    }
    return () => clearTimeout(id);
  }, [loadingStalls, loadingRealEstate]);

  // ---------- handlers ----------
  const handleStallCardClick = useCallback(
    (id: string) => navigate(`/stalls/${id}`),
    [navigate]
  );

  const handleCategorySelect = useCallback(
    (cat: string) => {
      setSelectedCategory(cat);
      if (!isDesktop) {
        setIsMarketplaceSidebarOpen(false);
        setIsRealEstateSidebarOpen(false);
      }
    },
    [setSelectedCategory, isDesktop]
  );

  // ---------- render helpers ----------
  const renderJobOpportunities = () => (
    <>
      {jobsError && <ErrorMessage>{jobsError}</ErrorMessage>}
      {jobAdverts.length === 0 && !loadingJobs && !jobsError && (
        <NoPostsMessage>No active job opportunities currently.</NoPostsMessage>
      )}
      <JobOpportunitiesGrid>
        {jobAdverts.map((job) => (
          <JobOpportunityCard key={job.id}>
            <JobOpportunityTitle>{job.jobTitle}</JobOpportunityTitle>
            <JobOpportunityMeta><strong>Organization:</strong> {job.organization}</JobOpportunityMeta>
            <JobOpportunityMeta><strong>Duty Station:</strong> {job.dutyStation}</JobOpportunityMeta>
            <JobOpportunityMeta><strong>Deadline:</strong> {job.applicationDeadline}</JobOpportunityMeta>
            <JobOpportunityText>{job.description}</JobOpportunityText>
            {job.contactNumber && (
              <JobOpportunityMeta><strong>Contact:</strong> {job.contactNumber}</JobOpportunityMeta>
            )}
          </JobOpportunityCard>
        ))}
      </JobOpportunitiesGrid>
    </>
  );

  const renderContent = () => {
    if (selectedCategory === 'Job Opportunities') {
      return renderJobOpportunities();
    }

    const regular = filteredAndSortedItems.filter((i) => !isStall(i) || !i.isPremium);

    return (
      <>
        {shuffledPremiumStalls.length > 0 && (
          <>
            <CenteredHeading>Premium Businesses Adverts</CenteredHeading>
            <PremiumStallsGrid>
              {shuffledPremiumStalls.map((stall, idx) => {
                const key = idx === 0 ? `${stall.id}-${shuffleCount}` : stall.id;
                return (
                  <PremiumStallCard
                    key={key}
                    stall={stall}
                    handleCardClick={handleStallCardClick}
                    handleImageClick={() => {}}
                    shuffleCount={shuffleCount}
                    shouldFlip={idx === 0}
                  />
                );
              })}
            </PremiumStallsGrid>
          </>
        )}

        {regular.length > 0 && (
          <>
            <CenteredHeading>All Listings</CenteredHeading>
            <ListingsGrid>
              {regular.map((item) => {
                if (isStall(item)) {
                  return (
                    <StallCard
                      key={item.id}
                      stall={item}
                      handleCardClick={handleStallCardClick}
                      handleImageClick={() => {}}
                    />
                  );
                }
                const r = item as RealEstateListing;
                const url = r.category
                  .toLowerCase()
                  .replace(/[^a-z0-9]/g, '_')
                  .replace(/_{2,}/g, '_');
                return (
                  <Link key={r.id} to={`/real-estate/${url}/${r.id}`} style={{ textDecoration: 'none' }}>
                    <RealEstateCard listing={r} />
                  </Link>
                );
              })}
            </ListingsGrid>
          </>
        )}
      </>
    );
  };

  const renderListings = () => {
    const loading = loadingStalls || loadingRealEstate || loadingJobs;
    const error = stallsError || realEstateError || jobsError;
    const hasContent = filteredAndSortedItems.length > 0 || shuffledPremiumStalls.length > 0 || jobAdverts.length > 0;

    if ((loading || (selectedCategory === 'Job Opportunities' && loadingJobs)) && !hasContent && !loadingTimeout) {
      return (
        <SkeletonGrid>
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonCard key={i}>
              <SkeletonImage />
              <SkeletonText width="80%" />
              <SkeletonText width="60%" />
              <SkeletonText width="40%" />
            </SkeletonCard>
          ))}
        </SkeletonGrid>
      );
    }

    if (loading && hasContent) {
      return (
        <>
          <LoadingMessage>
            <Spinner />Loading more content...
          </LoadingMessage>
          {renderContent()}
        </>
      );
    }

    if (loading && loadingTimeout) {
      return (
        <LoadingMessage>
          <Spinner />Loading Please wait...
        </LoadingMessage>
      );
    }

    return renderContent();
  };

  // ---------- early returns ----------
  if (loadingFirebase) return <LoadingMessage>Welcome to GREMA ...</LoadingMessage>;
  if (firebaseError) return <ErrorMessage>Authentication Error: {firebaseError}</ErrorMessage>;

  // ---------- JSX ----------
  return (
    <HomeWrapper>
      <UnifiedSearchBarWrapper>
        <SearchBarContainer>
          <SearchIcon>
            <FontAwesomeIcon icon={faSearch} />
          </SearchIcon>
          <SearchInput
            type="text"
            placeholder="Search all businesses by name, description, location, and more..."
            value={globalSearchQuery}
            onChange={(e) => setGlobalSearchQuery(e.target.value)}
          />
        </SearchBarContainer>

        <LocationSelectContainer>
          <LocationIcon icon={faMapMarkerAlt} />
          <SearchInput
            type="text"
            placeholder="Search location..."
            value={locationSearchQuery}
            onChange={(e) => {
              const v = e.target.value;
              setLocationSearchQuery(v);
              setSelectedLocation(allLocations.includes(v) ? v : 'All Locations');
            }}
            onFocus={() => setSelectedLocation('All Locations')}
          />
          {locationSearchQuery && allLocations.length > 1 && (
            <LocationDropdown>
              {allLocations.map((loc) => (
                <LocationOption
                  key={loc}
                  onClick={() => {
                    setLocationSearchQuery(loc);
                    setSelectedLocation(loc);
                  }}
                >
                  {loc}
                </LocationOption>
              ))}
            </LocationDropdown>
          )}
        </LocationSelectContainer>
      </UnifiedSearchBarWrapper>

      <SidebarToggleContainer>
        <SidebarToggle
          onClick={() => {
            setIsRealEstateSidebarOpen(false);
            setIsMarketplaceSidebarOpen(!isMarketplaceSidebarOpen);
          }}
          $isActive={isMarketplaceSidebarOpen}
        >
          {isMarketplaceSidebarOpen ? 'Hide Businesses' : 'All Businesses Adverts'}
        </SidebarToggle>
        <SidebarToggle
          onClick={() => {
            setIsMarketplaceSidebarOpen(false);
            setIsRealEstateSidebarOpen(!isRealEstateSidebarOpen);
          }}
          $isActive={isRealEstateSidebarOpen}
        >
          {isRealEstateSidebarOpen ? 'Hide Real Estate' : 'GREMA Real Estate & Properties'}
        </SidebarToggle>
      </SidebarToggleContainer>

      <MainLayout>
        <MarketplaceSidebarWrapper $isOpen={isDesktop || isMarketplaceSidebarOpen}>
          <MarketplaceSidebar
            categories={stallCategories}
            selectedCategory={selectedCategory}
            onCategorySelect={handleCategorySelect}
            title="All Businesses Adverts"
            onClose={() => setIsMarketplaceSidebarOpen(false)}
            categoryCounts={persistentCategoryCounts}
            totalInSection={totalStalls}
          />
        </MarketplaceSidebarWrapper>

        <RealEstateSidebarWrapper $isOpen={isDesktop || isRealEstateSidebarOpen}>
          <RealEstateSidebarComponent
            categories={realEstateCategories}
            selectedCategory={selectedCategory}
            onCategorySelect={handleCategorySelect}
            title="GREMA Real Estate & Properties"
            onClose={() => setIsRealEstateSidebarOpen(false)}
            categoryCounts={persistentCategoryCounts}
            totalInSection={totalRealEstate}
          />
        </RealEstateSidebarWrapper>

        <ContentContainer>{renderListings()}</ContentContainer>
      </MainLayout>      
      <FloatingButtonLeftContainer>
        <FloatingButton onClick={() => navigate('/feedback')} style={{ background: '#f56565' }}>
          Testimonies
        </FloatingButton>
      </FloatingButtonLeftContainer>

      <FloatingButtonsContainer>
        <FloatingButton onClick={() => navigate('/buy-from-grema')}>
          Buy through GREMA
        </FloatingButton>
        <FloatingButton onClick={() => navigate('/suppliers')}>
          Supply to GREMA!
        </FloatingButton>
        <FloatingButton onClick={() => navigate('/sell-to-grema')}>
          Sell through GREMA
        </FloatingButton>
      </FloatingButtonsContainer>

      {showWelcomePopup && (
  <WelcomePopupOverlay onClick={closeWelcomePopup}>
    <WelcomePopupContent onClick={(e) => e.stopPropagation()}>
      <WelcomeCloseButton onClick={closeWelcomePopup}>✕</WelcomeCloseButton>
      <WelcomeTitle>Advertise with GREMA</WelcomeTitle>
      <WelcomeMessage>
        Advertise your business with GREMA and connect your business to our global network of customers
      </WelcomeMessage>
      <WelcomeButton onClick={() => navigate('/buy-stall')}>
        Get Started – Buy a Stall
      </WelcomeButton>
    </WelcomePopupContent>
  </WelcomePopupOverlay>
)}
    </HomeWrapper>
  );
};
